import {
  Box,
  Divider,
  FormHelperText,
  MenuItem,
  type SelectChangeEvent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useKeyValues } from 'api/keyValues';
import type { ExposureOrGroup } from 'api/submission';
import {
  APPLICATION_STEPS,
  defaultBackendDateformat,
  singleSelectableDrivingIncidentFieldNames,
  VALIDATION_MESSAGES,
} from 'common/constants';
import { Modal } from 'components/Modal';
import { DatePicker } from 'components/lib/DatePicker';
import { FormCheckbox } from 'components/lib/FormCheckbox';
import { FormControl } from 'components/lib/FormControl';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import type { Field } from 'components/lib/QuestionEngine';
import { Select } from 'components/lib/Select';
import { SkeletonList } from 'components/lib/SkeletonList';
import { TextField } from 'components/lib/TextField';
import { type FormikTouched, useFormik } from 'formik';
import { formatDate } from 'helpers/utils';
import { useExposuresOrGroupFields } from 'hooks/useExposuresOrGroupFields';
import { useScrollToFirstErroredInput } from 'hooks/useScrollToFirstErroredInput';
import { isEmpty } from 'lodash-es';
import { DateTime } from 'luxon';
import {
  type ChangeEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import type { IDriver } from 'routes/Exposures/Drivers/Drives.types';
import * as yup from 'yup';
import type { IDrivingIncident, IDrivingIncidentsType } from '../../DrivingIncidents.types';
import {
  DrivingIncidentsModalDriverSelectMenuItemStyle,
  DrivingIncidentsModalDriverSelectStyle,
  DrivingIncidentsModalToggleButtonsGroupDividerStyle,
  DrivingIncidentsModalToggleButtonsGroupTitleStyle,
} from './DrivingIncidentsModal.styles';
import type {
  DrivingIncidentsModalProps,
  DrivingIncidentsModalRef,
} from './DrivingIncidentsModal.types';

type TGroup = ExposureOrGroup<IDrivingIncident>;

const { fields: initialFields } = APPLICATION_STEPS['driving-incidents'];

const incidentsDescTriggerOptions =
  initialFields?.find((field) => field.code === 'pol_incidents_desc')?.additional_data
    ?.otherDescConditionalFieldname?.value || [];

const DrivingIncidentsModal = forwardRef<
  DrivingIncidentsModalRef | undefined,
  DrivingIncidentsModalProps
  // eslint-disable-next-line complexity
>(({ handleAdd, handleUpdate, category }, ref) => {
  const [editingGroup, setEditingGroup] = useState<TGroup>({} as TGroup);

  const { relatedResource: drivers } = useExposuresOrGroupFields<IDriver>({
    type: 'group',
    name: 'pol_drivers',
  });

  const themeHook = useTheme();

  const isMobileLayout = useMediaQuery(themeHook.breakpoints.down('sm'));

  const skeletonHeight = isMobileLayout ? 43.13 : 46;

  const { relatedResource: incidents, submission } = useExposuresOrGroupFields<IDrivingIncident>({
    type: 'group',
    name: 'pol_incidents',
  });

  const fillingSetId = useMemo(() => {
    const locations = submission?.data.exposures.location;
    return locations?.length ? locations.at(-1)?.data.loc_filing_set_id : undefined;
  }, [submission?.data.exposures.location]);

  const { data: drivingIncidentTypes, isLoading: drivingIncidentTypesLoading } = useKeyValues<
    IDrivingIncidentsType[]
  >({
    key: 'cavio',
    params: { search__FilingSetID: fillingSetId },
  });

  const [open, setOpen] = useState(false);

  const isEditing = !isEmpty(editingGroup);

  const title = `${isEditing ? 'Update' : 'Add'} ${category}`;

  const [state, setState] = useState({} as IDrivingIncident);

  const fields = useMemo(
    () =>
      initialFields.reduce(
        (acc, field) => {
          if (field.code === 'pol_incidents_driver') {
            const choices = drivers.map((driver) => ({
              name: `${driver.data.pol_drivers_first} ${driver.data.pol_drivers_last}`,
              code: `${driver.data.pol_drivers_id};${incidents.length}`,
            }));
            acc[field.code] = { ...field, choices };
          } else if (field.code === 'pol_incidents_type') {
            if (!isEmpty(drivingIncidentTypes)) {
              const lastSingleSelectableDrivingIncidentFieldName =
                singleSelectableDrivingIncidentFieldNames.at(-1);
              const polIncidentsTypes = drivingIncidentTypes.value?.map((incType) => ({
                code: incType.VioDescription,
                name: incType.VioDescription,
                is_optional: true,
                is_hidden: false,
                is_readonly: false,
                newline: false,
                type: 'checkbox',
                // add divider after the last singleSelectableDrivingIncidentFieldName
                ...(incType.VioDescription === lastSingleSelectableDrivingIncidentFieldName
                  ? { additional_data: { divider: true } }
                  : {}),
              }));
              // sort polIncidentsTypes by name then put singleSelectableDrivingIncidentFieldNames at the top as it ordered in the array
              const sortedPolIncidentsTypes = polIncidentsTypes?.sort((a, b) => {
                const aIndex = singleSelectableDrivingIncidentFieldNames.indexOf(a.name);
                const bIndex = singleSelectableDrivingIncidentFieldNames.indexOf(b.name);
                if (aIndex > -1 && bIndex > -1) {
                  return aIndex - bIndex;
                }
                if (aIndex > -1) {
                  return -1;
                }
                if (bIndex > -1) {
                  return 1;
                }
                return a.name.localeCompare(b.name);
              });

              acc[field.code] = { ...field, nested_fields: sortedPolIncidentsTypes as Field[] };
            } else {
              acc[field.code] = field;
            }
          } else {
            acc[field.code] = field;
          }

          return acc;
        },
        {} as {
          [key in keyof IDrivingIncident]: Field;
        },
      ),
    [drivers, incidents, drivingIncidentTypes],
  );

  const formik = useFormik<IDrivingIncident>({
    initialValues: {
      ...state,
    },
    validationSchema: yup.lazy(() =>
      yup.object({
        pol_incidents_driver: yup.string().required(),
        pol_incidents_date: yup
          .date()
          .typeError(VALIDATION_MESSAGES.invalidDateFormat)
          .max(DateTime.now().startOf('day').toJSDate(), `Date must be earlier than today`)
          .required(),
        pol_incidents_type: yup
          .string()
          .test('empty', 'Choose at least one option', (val) => !isEmpty(val)),
      }),
    ),
    onSubmit: (_values) => {},
  });

  const validateFormik = async () => {
    await formik.submitForm();
    const errors = await formik.validateForm();

    if (!isEmpty(errors)) {
      await formik.setTouched(
        {
          ...formik.touched,
          ...Object.keys(fields).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {}),
        } as FormikTouched<IDrivingIncident>,
        false,
      );

      return false;
    }

    return true;
  };

  useImperativeHandle(ref, () => ({
    openModal: (group) => {
      if (group) {
        setEditingGroup(group);
        setState?.(group.data);
        formik?.setValues(group.data);
      }

      setOpen(true);
    },
  }));

  const resetModal = () => {
    setOpen(false);
    formik?.resetForm();
    setState?.({} as IDrivingIncident);
    setEditingGroup({} as TGroup);
  };

  const handleAction = async () => {
    const isValid = await validateFormik();

    if (!isValid) {
      return false;
    }

    const values: IDrivingIncident = {
      ...formik.values,
      pol_incidents_date: formik.values.pol_incidents_date
        ? formatDate(formik.values.pol_incidents_date, defaultBackendDateformat)
        : '',
    };

    if (isEditing) {
      handleUpdate(values, editingGroup?.id);
    } else {
      handleAdd(values);
    }

    resetModal();
    return true;
  };

  const updateState = (name: string, value: unknown) => {
    setState?.((prevState) => ({ ...prevState, [name]: value }));

    formik?.setFieldValue(name, value);
  };

  const handlePickChange = (value: Date | null) => {
    updateState(fields.pol_incidents_date.code, value);
  };

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>,
  ) => {
    const { name, value } = event.target;

    updateState(name, value);
  };

  const handlCheckboxChange = (checked: boolean, choice: Field) => {
    let newValues = (state?.[fields.pol_incidents_type.code] as string)?.split(';') || [];

    if (checked) {
      if (singleSelectableDrivingIncidentFieldNames.includes(String(choice.name))) {
        newValues = newValues.filter((x) => !singleSelectableDrivingIncidentFieldNames.includes(x));
      }

      newValues.push(choice.name!);
    }

    if (!checked) {
      newValues = newValues.filter((x: string) => x !== choice.name);
    }

    const updatedValue = newValues.join(';');

    formik.setFieldValue(fields.pol_incidents_type.code, updatedValue);

    setState?.((prevState) => ({ ...prevState, [fields.pol_incidents_type.code]: updatedValue }));
  };

  const showIncidentsDesc = useMemo(
    () => incidentsDescTriggerOptions?.some((option) => state.pol_incidents_type?.includes(option)),

    [state.pol_incidents_type?.length],
  );

  useEffect(() => {
    if (!showIncidentsDesc && state[fields.pol_incidents_desc.code]) {
      setState?.((prevState) => ({ ...prevState, [fields.pol_incidents_desc.code]: '' }));
    }
  }, [showIncidentsDesc, state[fields.pol_incidents_desc.code]]);

  useScrollToFirstErroredInput({
    formik,
  });

  return (
    <Modal
      open={open}
      title={title}
      onClose={resetModal}
      onCloseIconClick={() => resetModal()}
      modalContent={
        <Stack gap={1.5}>
          <Typography variant="h2" textAlign="justify">
            List all accidents and violations within the last 3 years. Do not include parking
            violations.
          </Typography>

          <form noValidate>
            <Stack gap={1.5}>
              <FormControl
                error={
                  formik.touched[fields.pol_incidents_driver.code] &&
                  Boolean(formik.errors[fields.pol_incidents_driver.code])
                }
                errorMessage={formik.errors[fields.pol_incidents_driver.code] as string}
                renderLabel
                field={fields.pol_incidents_driver}
              >
                <Select
                  id={fields.pol_incidents_driver.code}
                  name={fields.pol_incidents_driver.code}
                  labelId={`${fields.pol_incidents_driver.code}_label`}
                  label={fields.pol_incidents_driver.name}
                  value={state[fields.pol_incidents_driver.code]}
                  isFieldOptional={fields?.pol_incidents_driver?.is_optional}
                  renderValue={(value) => {
                    const selectedDriver = fields.pol_incidents_driver.choices?.find(
                      (driver) => driver.code.split(';')[0] === (value as string).split(';')[0],
                    );

                    return selectedDriver?.name || '';
                  }}
                  error={
                    formik.touched[fields.pol_incidents_driver.code] &&
                    Boolean(formik.errors[fields.pol_incidents_driver.code])
                  }
                  onChange={handleInputChange}
                  sx={DrivingIncidentsModalDriverSelectStyle}
                  optionsCount={fields.pol_incidents_driver.choices?.length}
                  preSelectFirstOption={() => {
                    const firstChoice = fields.pol_incidents_driver.choices?.at(0);
                    if (firstChoice) {
                      updateState(fields.pol_incidents_driver.code!, firstChoice?.code);
                    }
                  }}
                >
                  {(fields.pol_incidents_driver.choices || []).map(({ name, code }) => (
                    <MenuItem
                      key={code}
                      value={code}
                      sx={DrivingIncidentsModalDriverSelectMenuItemStyle}
                    >
                      <span>{name}</span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth id={fields.pol_incidents_date.code}>
                <DatePicker
                  value={(state[fields.pol_incidents_date.code] as Date) || null}
                  onChange={handlePickChange}
                  maxDate={DateTime.now().startOf('day').toJSDate()}
                  error={
                    formik.touched[fields.pol_incidents_date.code] &&
                    Boolean(formik.errors[fields.pol_incidents_date.code])
                  }
                  label={fields.pol_incidents_date.name}
                  slotProps={{
                    textField: {
                      error:
                        formik.touched[fields.pol_incidents_date.code] &&
                        Boolean(formik.errors[fields.pol_incidents_date.code]),
                      helperText:
                        formik.touched[fields.pol_incidents_date.code] &&
                        Boolean(formik.errors[fields.pol_incidents_date.code]) &&
                        (formik.errors[fields.pol_incidents_date.code] as string),
                      inputProps: {
                        'data-test': 'pol_incidents_date',
                      },
                    },
                  }}
                />
              </FormControl>
            </Stack>

            <Stack gap={2}>
              <FormControl fullWidth id={fields.pol_incidents_type.code}>
                <Stack gap={1.5}>
                  <Typography variant="h2" sx={DrivingIncidentsModalToggleButtonsGroupTitleStyle}>
                    Check all that apply
                    {formik.touched[fields.pol_incidents_type.code] &&
                      Boolean(formik.errors[fields.pol_incidents_type.code]) && (
                        <FormHelperText>
                          {formik.errors[fields.pol_incidents_type.code] as string}
                        </FormHelperText>
                      )}
                  </Typography>

                  {drivingIncidentTypesLoading ? (
                    <Box>
                      <SkeletonList count={7} height={skeletonHeight} />
                    </Box>
                  ) : (
                    fields.pol_incidents_type.nested_fields?.map((choice) => (
                      <Box key={choice.code}>
                        <FormCheckbox
                          labelText={choice.name as string}
                          id={choice.code}
                          name={choice.name}
                          checked={
                            (state?.[fields.pol_incidents_type.code] as string)?.includes(
                              choice.name!,
                            ) ?? false
                          }
                          onChange={(_, checked) => handlCheckboxChange(checked, choice)}
                        />

                        {choice.additional_data?.divider && (
                          <Divider
                            orientation="horizontal"
                            flexItem
                            sx={DrivingIncidentsModalToggleButtonsGroupDividerStyle}
                          />
                        )}
                      </Box>
                    ))
                  )}
                </Stack>
              </FormControl>

              {showIncidentsDesc && (
                <FormControl fullWidth>
                  <TextField
                    id={fields.pol_incidents_desc.code}
                    name={fields.pol_incidents_desc.code}
                    label={fields.pol_incidents_desc.name}
                    size="small"
                    multiline
                    rows={3}
                    field={fields.pol_incidents_desc}
                    isFieldOptional={fields.pol_incidents_desc.is_optional}
                    required={!fields.pol_incidents_desc.is_optional}
                    onChange={handleInputChange}
                    value={state.pol_incidents_desc || ''}
                    error={
                      formik.touched[`${fields.pol_incidents_desc.code}`] &&
                      Boolean(formik.errors[`${fields.pol_incidents_desc.code}`])
                    }
                    helperText={
                      formik.touched[`${fields.pol_incidents_desc.code}`] &&
                      (formik.errors[`${fields.pol_incidents_desc.code}`] as string)
                    }
                  />
                </FormControl>
              )}
            </Stack>
          </form>
        </Stack>
      }
      modalFooter={
        <PrimaryButton text={title} fullWidth onClick={handleAction} data-test="add-incident" />
      }
    />
  );
});

export default DrivingIncidentsModal;
